/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */

/* eslint-disable max-lines */
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonColour,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { TFunction } from 'i18next';
import { isUndefined } from 'lodash';
import React, { Fragment, ReactNode } from 'react';

import { Market } from '@toucan/config/markets';

import { SiteContentMetaDataResponse } from '@toucan/services/contentful/meta';
import {
    SiteContentHeader,
    SiteContentHeaderResponse,
} from '@toucan/services/contentful/site-content-header';

import {
    getContentfulLinkDetails,
    getEntryLinkDetailsByTypename,
} from '@toucan/helpers/contentful';
import { getFormattedDateTime } from '@toucan/helpers/date';

import { SitewideBannerColours } from '@toucan/components/organisms/layout/components/sitewide-banner';

import {
    CollapsibleAside,
    CollapsibleAsideVariant,
} from '@toucan/client/blocks/components/collapsible-aside';
import {
    HeroBannerProps,
    HeroBannerVariant,
} from '@toucan/client/blocks/components/hero-banner';
import { Row, RowStackVariant } from '@toucan/client/blocks/components/row';
import {
    ArticleCardData,
    BlockEntry,
    CmsBasicPageData,
    ContentAreaFields,
    ContentBlockCollection,
    ContentHubCategoryData,
    ContentfulCallToAction,
    ContentfulTableauDashboardProps,
    HyperlinkEntry,
    Links,
    RichText,
    SiteContentAccordion,
    SiteContentContentCollection,
    SiteContentEmbed,
    SiteContentEnhancedLink,
    SiteContentScript,
    SiteContentSectionPreviewBlock,
    SiteHeaderFields,
    SiteModalBlockFields,
    SitewideBannerData,
    TwoColumnWrapper,
} from '@toucan/client/core/contentful/config/types';
import { SiteContentVideoBlock } from '@toucan/client/core/contentful/helpers/queries/video-block';
import {
    ConditionModalContent,
    ContentfulPicture,
    defaultDocumentOptions,
    getRenderers,
} from '@toucan/client/features/contentful';
import { Blockquote } from '@toucan/client/features/contentful/components/blockquote';
import { CallToAction } from '@toucan/client/features/contentful/components/call-to-action';
import { ContentCollection } from '@toucan/client/features/contentful/components/content-collection';
import { EmbeddedScript } from '@toucan/client/features/contentful/components/embedded-script';
import { StoryCard } from '@toucan/client/features/contentful/components/story-card';
import { TableauDashboard } from '@toucan/client/features/contentful/components/tableau-dashboard';
import {
    EntryType,
    LandingPageContent,
    LandingPageContentResponse,
} from '@toucan/client/features/contentful/config/page-data';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { Column, ColumnVariant } from '@toucan/client/shared/components/column';
import { Link } from '@toucan/client/shared/components/link';
import { QuestionModalContent } from '@toucan/client/shared/components/question-modal-content';
import { renderMarkdown } from '@toucan/client/shared/helpers/markdown/markdown';
import { SectionPreviewBlock } from '@toucan/features/contentful/components/section-preview-block';
import { VideoBlock } from '@toucan/features/contentful/components/video-block';

const documentOptions = defaultDocumentOptions;

/**
 * Converts rich text document to React nodes with the correct document options
 *
 * @param document The document to convert
 */
export function documentToReactNode(document: Document) {
    return documentToReactComponents(document, documentOptions);
}

export function findEntry(
    node: any,
    links?: Links,
    entryType: EntryType = EntryType.BLOCK
): BlockEntry | undefined {
    const id = node?.data?.target?.sys?.id;

    if (isUndefined(id)) {
        return;
    }

    const entry: BlockEntry | undefined = links?.entries?.[entryType]?.find(
        (entry: BlockEntry) => entry?.sys?.id === id
    );

    return entry;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface SiteHeaderToHeroBlockOptions {
    /**
     * If skeletons should be rendered when data is loading
     * @default true
     */
    withSkeletons?: boolean;

    /**
     * The variant of the banner whilst loading
     *
     * @default HeroBannerVariant.DARK
     */
    loadingVariant?: HeroBannerVariant;

    /**
     * The colour of the loading skeletons
     */
    skeletonColour?: SkeletonColour;

    /**
     * A function that allows the description from contentful to be amended
     * E.g adding a trustpilot widget after the text
     */
    renderContent?: (content: ReactNode) => ReactNode;

    imageOptions?: {
        fetchPriority?: 'high' | 'low';
    };
}

/**
 * Converts site header data into a valid hero image. If the data is not
 * present, default values are returned instead
 *
 * @param {SiteHeaderFields} data The site header data to return
 * @param {SiteHeaderToHeroBlockOptions} userOptions
 * @param {boolean} withSkeletons Should skeletons be shown
 * @return {HeroBannerProps} formatted response to pass to HeroBanner
 */
export function siteHeaderToHeroBlock(
    data?: SiteHeaderFields | SiteContentHeader,
    userOptions: SiteHeaderToHeroBlockOptions = {}
): HeroBannerProps {
    const defaultOptions: Required<
        Omit<SiteHeaderToHeroBlockOptions, 'imageOptions'>
    > = {
        withSkeletons: true,
        loadingVariant: HeroBannerVariant.NO_BACKGROUND,
        skeletonColour: SkeletonColour.LIGHT,
        renderContent: (node) => node,
    };

    const options = {
        ...defaultOptions,
        ...userOptions,
    };

    const { withSkeletons, loadingVariant, skeletonColour, renderContent } =
        options;

    const variantMap = {
        HERO_GRAPHIC: HeroBannerVariant.GRAPHIC,
        HERO_IMAGE: HeroBannerVariant.LIGHT,
        HERO_GENERAL: HeroBannerVariant.NO_BACKGROUND,
    };

    const links = data?.description?.links;

    const renderOptions = {
        ...documentOptions,
        renderNode: {
            ...documentOptions.renderNode,
            [INLINES.EMBEDDED_ENTRY]: getRenderers(EntryType.INLINE, links),
        },
    };

    const description = data?.description?.json
        ? documentToReactComponents(data.description?.json, renderOptions)
        : null;

    return {
        heading: data ? ( // eslint-disable-line no-nested-ternary
            data.title
        ) : withSkeletons ? (
            <Skeleton
                className="skeleton-wrapper"
                variant={SkeletonVariant.TEXT}
                colour={skeletonColour}
            />
        ) : null,
        image: (
            <ContentfulPicture
                image={data?.image ?? null}
                options={options.imageOptions}
            />
        ),
        // @ts-ignore - Element implicitly has an any type - Touch and Change
        variant: data ? variantMap[data.variant] : loadingVariant,
        subHeading: data ? ( // eslint-disable-line no-nested-ternary
            data.subtitle ? (
                documentToReactNode(data.subtitle.json)
            ) : null
        ) : withSkeletons ? (
            <Skeleton
                className="skeleton-wrapper"
                variant={SkeletonVariant.TEXT}
                colour={skeletonColour}
                lineCount={1}
            />
        ) : null,
        content: data ? renderContent(description) : null,
    };
}

/**
 * Converts modal data into a React Node
 *
 * @param data The data to transform
 * @param fallback The fallback node to display
 */
export function siteModalToModal(
    data?: SiteModalBlockFields,
    fallback: ReactNode = (
        <Skeleton
            className="skeleton-wrapper"
            variant={SkeletonVariant.TEXT}
            lineCount={3}
        />
    )
): ReactNode {
    const content = data?.content;
    const links = content?.links;
    const options = {
        ...documentOptions,
        renderNode: {
            ...documentOptions.renderNode,
            [INLINES.EMBEDDED_ENTRY]: getRenderers(EntryType.INLINE, links),
            [BLOCKS.EMBEDDED_ENTRY]: getRenderers(EntryType.BLOCK, links),
        },
    };

    return content?.json
        ? documentToReactComponents(content.json, options)
        : fallback;
}

export function siteModalToQuestionModal(
    data?: SiteModalBlockFields
): ReactNode {
    const fallback = (
        <Fragment>
            <Heading size={HeadingSize.H2}>
                <Skeleton
                    className="skeleton-wrapper"
                    variant={SkeletonVariant.TEXT}
                    lineCount={1}
                />
            </Heading>
            <Row>
                <Column variant={ColumnVariant.FLUID}>
                    <Skeleton
                        className="skeleton-wrapper"
                        variant={SkeletonVariant.TEXT}
                        lineCount={3}
                    />
                </Column>
                <Column variant={ColumnVariant.FLUID}>
                    <Skeleton
                        className="skeleton-wrapper"
                        variant={SkeletonVariant.TEXT}
                        lineCount={3}
                    />
                </Column>
            </Row>
        </Fragment>
    );

    const content = siteModalToModal(data, fallback);
    return <QuestionModalContent>{content}</QuestionModalContent>;
}

/**
 * Converts modal data that display the product conditions
 * into a React Node
 *
 * @param data The data to transform
 * @returns {ReactNode} The node to display in the modal
 */
export function siteModalToConditionsModal(
    data?: SiteModalBlockFields
): ReactNode {
    const fallback = (
        <div>
            <Heading size={HeadingSize.H2}>
                <Skeleton
                    className="skeleton-wrapper"
                    variant={SkeletonVariant.TEXT}
                    lineCount={1}
                />
            </Heading>
            <Skeleton
                className="skeleton-wrapper"
                variant={SkeletonVariant.TEXT}
                lineCount={5}
            />
        </div>
    );

    const content = siteModalToModal(data, fallback);
    return (
        <ConditionModalContent data-testid="conditions-modal">
            {content}
        </ConditionModalContent>
    );
}

export function contentfulContentAreaToAccountSplashPage(
    data?: ContentAreaFields
): ReactNode {
    if (!data) {
        return (
            <Fragment>
                <Skeleton
                    variant={SkeletonVariant.BLOCK}
                    className="account-image-skeleton"
                    data-testid="account-image-skeleton"
                />
                <Skeleton
                    variant={SkeletonVariant.TEXT}
                    lineCount={3}
                    data-testid="account-description-skeleton"
                />
            </Fragment>
        );
    }

    const item = data.contentCollection?.items?.[0];

    if (isUndefined(item)) {
        return null;
    }

    const links = item.description?.links;

    const options = {
        ...documentOptions,
        renderNode: {
            ...documentOptions.renderNode,
            [INLINES.EMBEDDED_ENTRY]: getRenderers(EntryType.INLINE, links),
        },
    };

    const description = item.description?.json
        ? documentToReactComponents(item.description?.json, options)
        : null;

    return (
        <Fragment>
            <ContentfulPicture image={item.image} className="account-image" />
            {description}
        </Fragment>
    );
}

/**
 * Converts StoryCardData recieved from contentful into a storyCard Component
 */
export function contentfulArticleCardDataToArticleCard(
    article: ArticleCardData,
    activeMarket: Market,
    t: TFunction,
    dataId?: string
): ReactNode {
    const { url, templatePath } = getContentfulLinkDetails(
        {
            categorySlug: article?.category?.urlSlug,
            __typename: article.__typename,
            urlSlug: article.urlSlug ?? '',
        },
        activeMarket.slug,
        t
    );

    const publishedAt =
        article.originallyPublishedAt ?? article.sys?.publishedAt;

    const formattedPublishedDate = getFormattedDateTime(
        activeMarket.iso,
        publishedAt,
        {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }
    );

    const readMoreLabel = t('Read more');
    const articleThumbnail = article.thumbnail?.url
        ? article.thumbnail
        : article.thumbnailFallback;

    return (
        <StoryCard
            key={article.sys?.id}
            title={article.title}
            teaser={article.teaser}
            thumbnail={
                <ContentfulPicture
                    image={articleThumbnail}
                    data-id={`${dataId}__thumbnail`}
                />
            }
            readMoreLink={{
                href: url,
                templatePath,
            }}
            readMoreLabel={readMoreLabel}
            publishedAt={formattedPublishedDate}
            data-testid="article-card"
            data-id={`${dataId}__article-card`}
        />
    );
}

export function contentfulTwoColumnWrapperToTwoColumn(data: TwoColumnWrapper) {
    const links = mergeLinks(
        data.columnOneContent?.links,
        data.columnTwoContent?.links
    );

    const options = {
        ...documentOptions,
        renderNode: {
            ...documentOptions.renderNode,
            [BLOCKS.EMBEDDED_ENTRY]: getRenderers(EntryType.BLOCK, links),
            [INLINES.EMBEDDED_ENTRY]: getRenderers(EntryType.INLINE, links),
        },
    };
    return (
        <Fragment>
            <Row
                stackVariant={RowStackVariant.SMALL_TABLET}
                stackGap="var(--layout-spacing-48)"
            >
                <Column variant={ColumnVariant.FLUID}>
                    {data.columnOneContent
                        ? documentToReactComponents(
                              data.columnOneContent.json,
                              options
                          )
                        : null}
                </Column>
                <Column variant={ColumnVariant.FLUID}>
                    {data.columnTwoContent
                        ? documentToReactComponents(
                              data.columnTwoContent.json,
                              options
                          )
                        : null}
                </Column>
            </Row>
        </Fragment>
    );
}

/**
 * Merges entry links into one object. Useful when documentToReactComponents
 * shares the same options for different fn calls
 */
function mergeLinks(...links: (Links | undefined)[]): Links {
    return links.reduce(
        (mergedLinks: Links, link) => {
            if (link !== undefined) {
                mergedLinks.assets.block = [
                    ...mergedLinks.assets.block,
                    ...(link?.assets?.block ?? []),
                ];
                mergedLinks.entries.hyperlink = [
                    ...mergedLinks.entries.hyperlink,
                    ...(link?.entries?.hyperlink ?? []),
                ];
                mergedLinks.entries.block = [
                    ...mergedLinks.entries.block,
                    ...(link?.entries?.block ?? []),
                ];
                mergedLinks.entries.inline = [
                    ...mergedLinks.entries.inline,
                    ...(link?.entries?.inline ?? []),
                ];
            }
            return mergedLinks;
        },
        {
            assets: {
                block: [],
            },
            entries: {
                hyperlink: [],
                block: [],
                inline: [],
            },
        }
    );
}
/**
 * function that creates the element for embedly to replace.
 * React has issues if the replaced a tag isn't wrapped with another element,
 * so the a tag is always wrapped with a span.
 */
export function contentfulSiteContentEmbed(entry: SiteContentEmbed) {
    return (
        <span>
            <a
                className="embedly-card"
                data-card-width="100%"
                data-card-controls="0"
                data-card-recommend="0"
                href={entry.embedTarget}
            >
                {entry.embedTarget}
            </a>
        </span>
    );
}

export function contentfulSiteContentCollection(
    entry: SiteContentContentCollection
) {
    if (!entry.contentBlocksCollection) {
        return null;
    }

    const items = entry.contentBlocksCollection.items.map(
        ({ image, title, description, link, linkTitle }) => {
            const options = {
                ...documentOptions,
                renderNode: {
                    ...documentOptions.renderNode,
                    [BLOCKS.EMBEDDED_ENTRY]: getRenderers(
                        EntryType.BLOCK,
                        description?.links
                    ),
                    [INLINES.EMBEDDED_ENTRY]: getRenderers(
                        EntryType.INLINE,
                        description?.links
                    ),
                },
            };

            return {
                title,
                image: <ContentfulPicture image={image} />,
                link,
                linkTitle,
                content: description?.json
                    ? documentToReactComponents(description?.json, options)
                    : null,
            };
        }
    );

    return (
        <ContentCollection
            variant={entry.renderType}
            heading={entry.heading}
            items={items}
        />
    );
}

export function renderContentfulCategoryContent(
    content: RichText | undefined,
    activeMarket: Market,
    t: TFunction
) {
    if (!content) {
        return null;
    }

    const links = content.links;

    const storyDocumentOptions = {
        ...documentOptions,
        renderNode: {
            ...documentOptions.renderNode,
            [INLINES.ENTRY_HYPERLINK]: (node: any) => {
                const id = node.data?.target?.sys?.id;

                if (isUndefined(id)) {
                    return null;
                }

                const entry: HyperlinkEntry | undefined =
                    links?.entries?.hyperlink.find(
                        (entry: HyperlinkEntry) => entry?.sys?.id === id
                    );

                if (!entry) {
                    return null;
                }

                const { url, templatePath } = getContentfulLinkDetails(
                    entry,
                    activeMarket.slug,
                    t
                );

                return (
                    <WrappedLink href={templatePath} as={url}>
                        {node.content?.[0]?.value}
                    </WrappedLink>
                );
            },
            [BLOCKS.EMBEDDED_ENTRY]: getRenderers(EntryType.BLOCK, links),
            [BLOCKS.QUOTE]: (node: any) => {
                return (
                    <Blockquote
                        quote={documentToReactComponents(node.content?.[0])}
                    />
                );
            },
        },
    };

    return content.json
        ? documentToReactComponents(content.json, storyDocumentOptions)
        : null;
}

export function contentfulCallToAction(entry: ContentfulCallToAction) {
    return <CallToAction data={entry} />;
}

export function contentfulLandingPageDataToContentBlocks(
    pageData: LandingPageContent | undefined,
    activeMarket: Market,
    t: TFunction
) {
    const content = pageData?.content;

    if (!content) {
        return null;
    }

    const links = content.links;

    const storyDocumentOptions = {
        ...documentOptions,
        renderNode: {
            ...documentOptions.renderNode,
            [INLINES.ENTRY_HYPERLINK]: contentfulEntryHyperlinkToWrappedLink(
                links,
                activeMarket,
                t
            ),
            [BLOCKS.EMBEDDED_ENTRY]: getRenderers(EntryType.BLOCK, links, {
                TableauDashboard: contentfulTableauDashboardToTableauDashboard,
            }),
            [INLINES.EMBEDDED_ENTRY]: getRenderers(EntryType.INLINE, links),
        },
    };

    return content.json
        ? documentToReactComponents(content.json, storyDocumentOptions)
        : null;
}

export function contentfulCategoryDataToCategoryCard(
    category: ContentHubCategoryData,
    marketSlug: string,
    t: TFunction
) {
    const { url, templatePath } = getContentfulLinkDetails(
        {
            __typename: category.__typename,
            urlSlug: category.urlSlug ?? '',
        },
        marketSlug,
        t
    );

    const readMoreLabel = t('See more');
    const categoryThumbnail = category.thumbnail?.url
        ? category.thumbnail
        : category.thumbnailFallback;

    return (
        <StoryCard
            key={category.sys.id}
            title={category.name}
            teaser={category.teaser}
            thumbnail={
                <ContentfulPicture image={categoryThumbnail} ratio={0.564} />
            }
            readMoreLink={{
                href: url,
                templatePath,
            }}
            readMoreLabel={readMoreLabel}
            data-testid="story-card"
        />
    );
}

/**
 * Converts sitewideBanner data into React node and returns the selected colour
 * @param {SitewideBannerData} data
 */
export function contentfulSitewideBannerDataToSitewideBanner(
    data: SitewideBannerData
): { content: ReactNode; colour: SitewideBannerColours; id: string } {
    // @ts-ignore - Element implicitly has an any type - Touch and Change
    const colour = SitewideBannerColours[data.theme];
    const content = documentToReactNode(data.message.json);
    const id = data.sys.id;

    return {
        content,
        colour,
        id,
    };
}

/**
 * Converts enhanced link data into React node
 * @param {SiteContentEnhancedLink} entry
 */
export function contentfulEnhancedLinkDataToEnhancedLink(
    entry: SiteContentEnhancedLink
) {
    if (!entry) {
        return null;
    }

    return (
        <Link href={entry.url} target={entry.target}>
            {entry.content}
        </Link>
    );
}

export function contentfulBasicPageDataToContentBlocks(
    pageData: CmsBasicPageData | undefined,
    marketSlug: string
) {
    const content = pageData?.content;

    if (!content) {
        return null;
    }

    const links = pageData.content.links;

    const options = {
        ...documentOptions,
        renderNode: {
            [INLINES.ENTRY_HYPERLINK]: (node: any) => {
                const entry = findEntry(node, links, EntryType.HYPERLINK);

                if (!entry) {
                    return null;
                }

                return (
                    <WrappedLink
                        href="/cms/basic"
                        as={`/${marketSlug}/${entry.urlSlug}`}
                    >
                        {node.content[0].value}
                    </WrappedLink>
                );
            },
            [INLINES.EMBEDDED_ENTRY]: getRenderers(EntryType.INLINE, links),
            [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
                const entry = findEntry(node, links);

                if (!entry) {
                    return null;
                }

                return (
                    <div className={entry.class} id={entry.id}>
                        {entry.content}
                    </div>
                );
            },
        },
    };

    return content.json
        ? documentToReactComponents(content.json, options)
        : null;
}

export function contentBlockCollectionToStoryCards(
    contentBlockCollection: ContentBlockCollection | undefined,
    marketSlug: string
) {
    if (!contentBlockCollection?.contentCollection?.items) {
        return null;
    }

    return contentBlockCollection?.contentCollection?.items.map(
        (contentBlock, index) => {
            const { title, image, link, description } = contentBlock;

            const teaser =
                description?.json && documentToReactNode(description?.json);

            const url = `/${marketSlug}/${link}`;

            return (
                <StoryCard
                    title={title}
                    thumbnail={<ContentfulPicture image={image} />}
                    teaser={teaser}
                    loading={false}
                    readMoreLink={{
                        // This interface is designed around articles, so
                        // templatePath + href are required though not
                        // actually needed in this case.
                        href: url,
                        templatePath: url,
                    }}
                    readMoreLabel={contentBlock?.linkTitle ?? undefined}
                    key={index}
                />
            );
        }
    );
}

export function contentfulContentAccordionToAccordion(
    data?: SiteContentAccordion
) {
    if (!data) {
        return null;
    }

    const title = data.title;
    const id = data.sys?.id;
    const content = data.content?.json;
    const links = data.content?.links;

    const options = {
        ...documentOptions,
        renderNode: {
            ...documentOptions.renderNode,
            [INLINES.EMBEDDED_ENTRY]: getRenderers(EntryType.INLINE, links),
        },
    };

    if (!content) {
        return null;
    }

    return (
        <CollapsibleAside
            data-testid="faq_accordion"
            className="accordion-faq"
            variant={CollapsibleAsideVariant.STANDALONE}
            key={id}
            title={title}
        >
            {documentToReactComponents(content, options)}
        </CollapsibleAside>
    );
}

export function transformSiteContentMetaData(
    data?: SiteContentMetaDataResponse
) {
    return {
        metaTitle: data?.siteContentMetaCollection?.items?.[0]?.title,
        metaDescription:
            data?.siteContentMetaCollection?.items?.[0]?.description,
    };
}

export function transformLandingPageCollection(
    data?: LandingPageContentResponse
) {
    return data?.landingPageCollection?.items?.[0];
}

export function scriptToScriptTag(data?: SiteContentScript) {
    if (!data?.src || !data?.sys?.id) {
        return null;
    }

    return (
        <EmbeddedScript
            attributes={data?.attributes}
            src={data.src}
            id={data.sys.id}
        />
    );
}

export function contentfulSectionPreviewBlockToSectionPreviewBlock(
    data: SiteContentSectionPreviewBlock
) {
    const {
        heading,
        sectionPreviewDescription: description,
        image,
        callToAction,
        variant,
        imagePosition,
    } = data;

    if (!image.url) {
        return null;
    }

    const imageComponent = (
        <ContentfulPicture image={image} widths={[300, 500]} />
    );

    return (
        <SectionPreviewBlock
            heading={heading}
            description={description}
            image={imageComponent ?? <img src={image.url} />}
            buttonHref={callToAction.buttonUrl}
            buttonText={callToAction.buttonLabel}
            variant={variant}
            imagePosition={imagePosition}
        />
    );
}

function contentfulTableauDashboardToTableauDashboard({
    src,
}: ContentfulTableauDashboardProps) {
    return <TableauDashboard src={src} />;
}

export function contentfulEntryHyperlinkToWrappedLink(
    links: Links | undefined,
    activeMarket: Market,
    t: TFunction
) {
    return (node: any) => {
        const id = node?.data?.target?.sys?.id;

        if (isUndefined(id)) {
            return null;
        }

        const entry: HyperlinkEntry | undefined =
            links?.entries?.hyperlink?.find(
                (entry: HyperlinkEntry) => entry?.sys?.id === id
            );

        if (!entry) {
            return null;
        }

        const entryLinkDetails = getEntryLinkDetailsByTypename(entry);

        const isUrlSlugEmpty =
            entryLinkDetails.urlSlug?.includes('untitled-entry');

        if (!entryLinkDetails.urlSlug || isUrlSlugEmpty) {
            return null;
        }

        const { url, templatePath } = getContentfulLinkDetails(
            entryLinkDetails,
            activeMarket.slug,
            t
        );

        return (
            <WrappedLink href={templatePath} as={url}>
                {node.content?.[0]?.value}
            </WrappedLink>
        );
    };
}

export function transformSiteContentHeader(data?: SiteContentHeaderResponse) {
    return data?.siteContentHeaderCollection?.items?.[0];
}

export function contentfulVideoBlockToVideoBlock(data?: SiteContentVideoBlock) {
    if (!data) return null;

    const links = data.description?.links;

    const {
        title,
        videoTitle,
        videoUrl,
        description,
        callToActionsCollection,
    } = data;

    const options = {
        ...documentOptions,
        renderNode: {
            ...documentOptions.renderNode,
            [INLINES.EMBEDDED_ENTRY]: getRenderers(EntryType.INLINE, links),
        },
    };

    return (
        <VideoBlock
            heading={renderMarkdown(title)}
            videoTitle={videoTitle}
            videoUrl={videoUrl}
            description={
                description &&
                documentToReactComponents(description.json, options)
            }
            callToActions={callToActionsCollection?.items}
        />
    );
}
